package ui.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import ui.components.Navbar
import ui.components.Footer as SiteFooter

@Composable
fun ExploreScreen(
    onBack: () -> Unit,
    onNavigateToTerms: () -> Unit = {},
    onNavigateToPrivacy: () -> Unit = {}
) {
    var activeIndex by remember { mutableStateOf(0) }
    var activeValidationIndex by remember { mutableStateOf(0) }
    var activeTutorialIndex by remember { mutableStateOf(0) }

    // Estado para el botón "Volver arriba"
    var showScrollTop by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        window.onscroll = {
            showScrollTop = window.scrollY > 500
            null
        }
    }

    // Lista de funcionalidades
    val features = listOf(
        FeatureData(
            "Tu lista de pacientes, organizada y a la mano",
            "Encuentra el historial de tus consultas al instante. Registra pacientes incluso sin conexión y deja que la app sincronice todo automáticamente al recuperar señal. Los iconos de estado te indicarán en tiempo real qué fichas están resguardadas en la nube y cuáles están pendientes, dándote total control sin depender del internet.",
            "explore_dashboard.webp"
        ),
        FeatureData(
            "Récipes, informes, reposos y paraclínicos en segundos",
            "Olvídate de la tediosa escritura manual y las firmas repetitivas. Genera récipes médicos, órdenes de estudios e informes estructurados rápidamente, listos para enviar o imprimir con un acabado profesional.",
            "explore_recipe.webp"
        ),
        FeatureData(
            "Tu identidad médica protegida y validada",
            "Configura tu firma y sello profesional una sola vez. Cada documento digital que emitas incluirá un código QR único de validación, garantizando la autenticidad de tus récipes frente a farmacias y pacientes.",
            "explore_profile.webp"
        ),
        FeatureData(
            "Tu archivo clínico digital, siempre a mano",
            "Accede a la cronología completa de salud de tu paciente. Desde un mismo lugar puedes revisar el detalle de consultas pasadas, editar información relevante o iniciar una nueva atención médica al instante. Es la herramienta definitiva para un seguimiento profesional y sin errores.",
            "explore_consultation.webp"
        ),
        FeatureData(
            "Marca personal con diseño profesional",
            "Dale a tus PDFs una identidad única. Configura el logo de tu preferencia, selecciona fuentes tipográficas modernas y aplica tus colores corporativos para un acabado impecable. Además, personaliza la interfaz de la aplicación con el tema dinámico, adaptando el color de tu fondo de pantalla móvil a la app Dr.Recipe.",
            "explore_marca.webp"
        )
    )

    // Lista de pasos de validación
    val validationSteps = listOf(
        FeatureData(
            "1. Respaldo legal y profesional",
            "Cada récipe, informe o reposo emitido incorpora un código QR único y tu firma digital preconfigurada. Esto blinda la autenticidad del documento, certificando tu identidad como médico verificado.",
            "validate_document_sample.webp"
        ),
        FeatureData(
            "2. Compatible con farmacias y entidades",
            "El código QR impreso o digital se lee al instante. Las farmacias y entes públicos pueden escanearlo directamente usando las cámaras de sus teléfonos o lectores de códigos comerciales estándar, sin necesidad de instalar aplicaciones extra.",
            "validate_app_qr.webp"
        ),
        FeatureData(
            "3. Validación antifraude al instante",
            "Al ser escaneado, el código redirige a nuestra plataforma web segura. Allí se muestran los datos originales de tu consulta, confirmando en tiempo real que el documento es legítimo y no ha sido alterado ni clonado.",
            "validate_web_screen.webp"
        )
    )

    // Lista de Tutoriales (Carousel)
    val tutorials = listOf(
        FeatureData(
            "Cómo verificar tu perfil profesional",
            "Para garantizar la validez legal de tus documentos y proteger tu identidad, validamos tus credenciales. Mira en este video cómo activar tu cuenta de forma rápida, segura y sin complicaciones.",
            "https://firebasestorage.googleapis.com/v0/b/dr-recipe-31eb8.firebasestorage.app/o/tutoriales%2Fdemosweb%2Fdemo_perfil_verificar.m4v?alt=media&token=4d88aee5-ef21-4972-b182-7ca1b8a9477d"
        ),
        FeatureData(
            "Cómo generar un récipe digital",
            "Descubre cómo emitir prescripciones médicas en segundos. Diseña el tratamiento desde tu Android y déjalo listo para enviarlo directamente al WhatsApp o correo de tu paciente, sin necesidad de imprimir.",
            "https://firebasestorage.googleapis.com/v0/b/dr-recipe-31eb8.firebasestorage.app/o/tutoriales%2Fdemosweb%2Fdemo%20web%20recipe.m4v?alt=media&token=071d914a-44b8-45f9-9224-cdc3bec164fe"
        ),
        FeatureData(
            "Cómo emitir un reposo médico",
            "Crea certificados de reposo estructurados y con total validez institucional. Configura los días y el diagnóstico con unos pocos toques, asegurando una presentación profesional y respaldada por tu firma y sello digital.",
            "https://firebasestorage.googleapis.com/v0/b/dr-recipe-31eb8.firebasestorage.app/o/tutoriales%2Fdemosweb%2Fdemo%20web%20reposo.m4v?alt=media&token=d08965b8-2d32-448b-b429-4d982554511d"
        ),
        FeatureData(
            "Órdenes de exámenes claras y legibles",
            "Genera solicitudes de estudios paraclínicos claras y detalladas. Facilita el trabajo de los laboratorios y centros de diagnóstico en Venezuela con formatos digitales perfectamente legibles.",
            "https://firebasestorage.googleapis.com/v0/b/dr-recipe-31eb8.firebasestorage.app/o/tutoriales%2Fdemosweb%2Fdemo%20web%20paraclinicos.m4v?alt=media&token=4810e0bc-f697-4c35-b5bf-877b075099e0"
        ),
        FeatureData(
            "Informes médicos rápidos",
            "Mira en este video cómo estructurar un reporte esencial en segundos. Solo ingresas el motivo de consulta, la enfermedad actual, hasta 5 diagnósticos y tus observaciones. Dale un toque al botón y listo: tu informe digital directo en tu Android, práctico y sin complicaciones.",
            "https://firebasestorage.googleapis.com/v0/b/dr-recipe-31eb8.firebasestorage.app/o/tutoriales%2Fdemosweb%2Fdemo%20web%20informe.m4v?alt=media&token=f958f256-1178-46d7-a597-b9a71a766bc8"
        )
    )

    Div(attrs = { classes("min-h-screen", "bg-white") }) {
        Navbar(onNavigateHome = onBack)

        // --- HERO SECTION ---
        Section(attrs = {
            classes("pt-32", "pb-16", "bg-white", "dark:bg-medical-bgDark", "relative", "overflow-hidden")
        }) {
            // Cambiado de px-6 a px-4 para coincidir con la alineación del Navbar y el LandingScreen
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4", "relative", "z-10") }) {
                Div(attrs = { classes("flex", "flex-col", "lg:flex-row", "items-start", "gap-8") }) {
                    // Texto
                    Div(attrs = { classes("flex-1") }) {
                        H1(attrs = { classes("text-4xl", "md:text-6xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mt-0", "mb-6", "leading-tight") }) {
                            Text("Evoluciona tu práctica médica con ")
                            Span(attrs = { 
                                style { property("color", "#80CBC4") }
                            }) { Text("récipes digitales seguros") }
                        }
                        P(attrs = { classes("text-xl", "text-gray-600", "dark:text-gray-400", "mb-10", "max-w-xl", "leading-relaxed") }) {
                            Text("Diseña e imprime prescripciones, informes y reposos médicos desde tu dispositivo Android. La solución eficiente y con validación QR pensada para el médico en Venezuela.")
                        }

                        // Botón de descarga Hero
                        A(href = "https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", attrs = {
                            attr("target", "_blank")
                            classes("inline-block", "transition-all", "hover:scale-105")
                        }) {
                            Img(src = "disponible_play.svg", attrs = { classes("h-16", "w-auto") })
                        }
                    }

                    // Personaje (Alineado con el título usando transformación de CSS)
                    Div(attrs = { 
                        classes("flex-1", "flex", "justify-center", "lg:justify-end", "mt-12", "lg:mt-0", "relative")
                    }) {
                        // Círculo sólido blanco centrado EXACTO con la imagen
                        Div(attrs = { 
                            classes("hidden", "dark:block", "absolute", "top-[-10px]", "lg:top-[-25px]", "right-1/2", "translate-x-[47%]", "lg:right-[-10px]", "lg:translate-x-0", "w-[305px]", "h-[305px]", "lg:w-[390px]", "lg:h-[390px]", "bg-white", "rounded-full", "z-0")
                        })

                        // Contenedor que aplica la animación "float" sin romper el transform interno de la imagen
                        Div(attrs = { classes("relative", "z-10", "animate-float") }) {
                            Img(src = "melia_heronew.webp", attrs = {
                                classes("block", "w-full", "h-auto", "max-w-[320px]", "lg:max-w-none")
                                style {
                                    // Estilos para Desktop (lg)
                                    if (window.innerWidth >= 1024) {
                                        property("width", "380px") 
                                        property("height", "550px") 
                                        property("transform", "translateY(-70px)") // Bajado de -140px
                                        property("margin-bottom", "-60px") // Ajustado de -140px
                                    } else {
                                        // Estilos para Mobile
                                        property("width", "100%")
                                        property("height", "auto")
                                    }
                                    property("object-fit", "cover") 
                                    property("object-position", "top")
                                }
                                attr("alt", "Asistente Dr. Recipe")
                            })
                        }
                    }
                }
            }
        }

        // --- HORIZONTAL SHOWCASE SECTION (Funciones) ---
        Section(attrs = { classes("py-24", "bg-gray-50", "dark:bg-medical-primaryDark", "overflow-hidden") }) {
            // Cambiado de px-6 a px-4 para mantener la línea guía
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-4") }) {
                // items-start en Desktop evita que la imagen baile cuando el texto cambia de altura
                Div(attrs = { classes("flex", "flex-col", "lg:flex-row", "items-start", "justify-between", "gap-12") }) {
                    
                    // Contenido Izquierda (Texto)
                    Div(attrs = { 
                        classes("flex-1", "max-w-xl", "text-left")
                    }) {
                        // Min-height escalonado con transición para suavizar cambios
                        Div(attrs = { 
                            classes("flex", "flex-col", "justify-start")
                            style { 
                                property("min-height", "450px") 
                                if (window.innerWidth >= 768) property("min-height", "320px")
                                if (window.innerWidth >= 1024) property("min-height", "280px")
                                property("transition", "min-height 0.3s ease")
                            }
                        }) {
                            key(activeIndex) {
                                Div(attrs = { classes("animate-fadeIn", "mb-12") }) {
                                    Span(attrs = { 
                                        classes("text-medical-primary", "dark:text-medical-bgDark/80", "font-bold", "text-sm", "uppercase", "tracking-widest", "mb-6", "block") 
                                    }) {
                                        Text("Función ${activeIndex + 1} de ${features.size}")
                                    }
                                    H2(attrs = { classes("text-2xl", "md:text-5xl", "font-bold", "text-gray-900", "dark:text-medical-bgDark", "mb-6", "md:mb-8") }) {
                                        Text(features[activeIndex].title)
                                    }
                                    P(attrs = { classes("text-lg", "md:text-xl", "text-gray-600", "dark:text-medical-bgDark/80", "leading-relaxed") }) {
                                        Text(features[activeIndex].description)
                                    }
                                }
                            }
                        }
                    }

                    // Imagen + Controles (Derecha / Abajo en Móvil)
                    Div(attrs = { 
                        classes("flex-1", "flex", "flex-col", "items-center", "lg:items-center", "mt-12", "lg:mt-0")
                    }) {
                        // El Teléfono - Espacio Reservado para evitar saltos
                        Div(attrs = { 
                            classes("relative", "shrink-0", "transition-transform", "duration-500", "hover:scale-105", "mb-10")
                            style {
                                property("width", "100%")
                                property("max-width", "230px")
                                property("aspect-ratio", "230/460")
                                property("min-height", "460px") // Reserva el espacio vertical exacto
                                property("height", "auto")
                            }
                        }) {
                            Img(src = "${features[activeIndex].imageSrc}?v=2", attrs = { 
                                style {
                                    property("width", "100%")
                                    property("height", "100%")
                                    property("object-fit", "contain")
                                }
                                classes("animate-fadeIn")
                                attr("alt", features[activeIndex].title)
                            })
                        }

                        // Controles - SIEMPRE DEBAJO DE LA IMAGEN
                        CarouselControls(
                            activeIndex = activeIndex,
                            itemsCount = features.size,
                            onPrev = { if (activeIndex > 0) activeIndex-- },
                            onNext = { if (activeIndex < features.size - 1) activeIndex++ },
                            onSelect = { activeIndex = it }
                        )
                    }
                }
            }
        }

        // --- VALIDATION SHOWCASE SECTION (QR) ---
        Section(attrs = { classes("py-24", "bg-white", "dark:bg-medical-surfaceDark", "overflow-hidden") }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-6") }) {
                // items-start en Desktop evita saltos verticales
                Div(attrs = { classes("flex", "flex-col", "lg:flex-row", "items-start", "justify-between", "gap-12") }) {
                    
                    // LADO TEXTO (Izquierda)
                    Div(attrs = { 
                        classes("flex-1", "max-w-xl", "text-left")
                    }) {
                        Div(attrs = { 
                            classes("flex", "flex-col", "justify-start")
                            style { 
                                property("min-height", "450px") 
                                if (window.innerWidth >= 768) property("min-height", "350px")
                                if (window.innerWidth >= 1024) property("min-height", "320px")
                                property("transition", "min-height 0.3s ease")
                            }
                        }) {
                            key(activeValidationIndex) {
                                Div(attrs = { classes("animate-fadeIn", "mb-12") }) {
                                    Span(attrs = { 
                                        classes("text-medical-primary", "dark:text-medical-successDark", "font-bold", "text-sm", "uppercase", "tracking-widest", "mb-6", "block") 
                                    }) {
                                        Text("Validación QR: Paso ${activeValidationIndex + 1} de ${validationSteps.size}")
                                    }
                                    H2(attrs = { classes("text-2xl", "md:text-5xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-6", "md:mb-8") }) {
                                        Text(validationSteps[activeValidationIndex].title)
                                    }
                                    P(attrs = { classes("text-lg", "md:text-xl", "text-gray-600", "dark:text-gray-400", "leading-relaxed") }) {
                                        Text(validationSteps[activeValidationIndex].description)
                                    }
                                }
                            }
                        }
                    }

                    // Imagen + Controles (Derecha)
                    Div(attrs = { 
                        classes("flex-1", "flex", "flex-col", "items-center", "lg:items-center", "mt-12", "lg:mt-0")
                    }) {
                        // Contenedor QR - Espacio Reservado
                        Div(attrs = { 
                            classes("relative", "shrink-0", "bg-gray-50", "rounded-[2.5rem]", "shadow-2xl", "p-4", "transition-transform", "duration-500", "hover:scale-105", "mb-10")
                            style {
                                property("height", "auto")
                                property("aspect-ratio", "500/340")
                                property("width", "100%")
                                property("max-width", "420px")
                                property("min-height", "286px")
                            }
                        }) {
                            Img(src = "${validationSteps[activeValidationIndex].imageSrc}?v=2", attrs = { 
                                style {
                                    property("width", "100%")
                                    property("height", "100%")
                                    property("object-fit", "contain")
                                    property("border-radius", "1.8rem")
                                }
                                classes("animate-fadeIn")
                                attr("alt", validationSteps[activeValidationIndex].title)
                            })
                        }

                        // Controles - SIEMPRE DEBAJO DE LA IMAGEN
                        CarouselControls(
                            activeIndex = activeValidationIndex,
                            itemsCount = validationSteps.size,
                            onPrev = { if (activeValidationIndex > 0) activeValidationIndex-- },
                            onNext = { if (activeValidationIndex < validationSteps.size - 1) activeValidationIndex++ },
                            onSelect = { activeValidationIndex = it }
                        )
                    }
                }
            }
        }

        // --- TUTORIALS SHOWCASE SECTION ---
        Section(attrs = { 
            classes("relative", "py-32", "overflow-hidden") 
            id("tutoriales")
        }) {
            // IMAGEN PARA DESKTOP (Efecto Parallax / Fixed)
            Div(attrs = {
                classes("hidden", "md:block", "absolute", "inset-0", "z-0")
                style {
                    property("background-image", "url('background_web3.png')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("background-attachment", "fixed") // Esto crea el movimiento al hacer scroll
                }
            })

            // IMAGEN PARA MÓVIL (Técnica "Magic Window")
            Div(attrs = {
                classes("md:hidden", "absolute", "inset-0", "z-0")
                style { property("clip-path", "inset(0)") }
            }) {
                Div(attrs = {
                    classes("fixed", "inset-0", "w-full", "h-full")
                    style {
                        property("background-image", "url('background_web3.png')")
                        property("background-size", "cover")
                        property("background-position", "center")
                        property("z-index", "-1")
                    }
                })
            }

            // Capa de superposición para contraste
            Div(attrs = { classes("absolute", "inset-0", "bg-medical-dark/10", "backdrop-brightness-90", "z-1") })

            Div(attrs = { classes("relative", "z-10", "max-w-7xl", "mx-auto", "px-6") }) {
                
                // Título de la sección (Estilo igual al del Landing)
                Div(attrs = { 
                    classes("text-center", "mb-16", "bg-white/30", "backdrop-blur-lg", "rounded-[2rem]", "p-8", "border", "border-white/10", "shadow-xl", "max-w-2xl", "mx-auto") 
                }) {
                    Span(attrs = { 
                        classes("text-medical-primary", "font-bold", "text-sm", "uppercase", "tracking-widest", "mb-4", "block") 
                    }) {
                        Text("Dr. Recipe")
                    }
                    H2(attrs = { classes("text-3xl", "md:text-5xl", "font-bold", "text-gray-900") }) {
                        Text("Demos de la App")
                    }
                }

                // Contenedor del Carrusel: Estilo unificado con el título
                Div(attrs = { 
                    classes("flex", "flex-col", "lg:flex-row", "items-start", "justify-between", "gap-12", "bg-white/30", "backdrop-blur-lg", "rounded-[3rem]", "p-8", "md:p-16", "shadow-2xl", "border", "border-white/10")
                }) {
                    
                    // Contenido Izquierda (Texto + Controles)
                    Div(attrs = { 
                        classes("flex-1", "max-w-xl", "text-left")
                    }) {
                        // Contenedor con altura mínima fija para evitar saltos de layout
                        Div(attrs = { 
                            classes("flex", "flex-col", "justify-start")
                            style { 
                                property("min-height", "480px") 
                                if (window.innerWidth >= 768) property("min-height", "380px")
                                if (window.innerWidth >= 1024) property("min-height", "320px")
                                property("transition", "min-height 0.3s ease")
                            }
                        }) {
                            key(activeTutorialIndex) {
                                Div(attrs = { classes("animate-fadeIn", "mb-12") }) {
                                    Span(attrs = { 
                                        classes("bg-medical-primary/20", "text-medical-primary", "px-4", "py-1", "rounded-full", "text-sm", "font-bold", "mb-6", "inline-block", "border", "border-medical-primary/30") 
                                    }) {
                                        Text(if (activeTutorialIndex == 0) "PASO FUNDAMENTAL" else "TUTORIAL $activeTutorialIndex")
                                    }
                                    H3(attrs = { classes("text-2xl", "md:text-4xl", "font-bold", "text-gray-900", "mb-6", "md:mb-8") }) {
                                        Text(tutorials[activeTutorialIndex].title)
                                    }
                                    P(attrs = { classes("text-lg", "md:text-xl", "text-gray-800", "leading-relaxed", "font-medium") }) {
                                        Text(tutorials[activeTutorialIndex].description)
                                    }
                                }
                            }
                        }
                    }

                    // Video (Lado Derecho) - REPRODUCTOR ANDROID PREMIUM
                    Div(attrs = { 
                        classes("flex-1", "flex", "flex-col", "items-center", "lg:items-center", "mt-12", "lg:mt-0")
                    }) {
                        // Mockup de Celular Android - Espacio Reservado
                        Div(attrs = { 
                            classes("relative", "shadow-2xl", "rounded-[2.5rem]", "bg-gray-800", "shrink-0", "border-[8px]", "border-gray-900", "overflow-hidden", "mb-8")
                            style {
                                property("width", "100%")
                                property("max-width", "230px")
                                property("aspect-ratio", "230/460")
                                property("min-height", "460px") // Reserva espacio para el video
                                property("height", "auto")
                            }
                        }) {
                            // El Video - Ahora es el elemento principal y con Z-INDEX ALTO
                            key(activeTutorialIndex) {
                                TagElement<org.w3c.dom.HTMLVideoElement>(
                                    tagName = "video",
                                    applyAttrs = {
                                        attr("controls", "true")
                                        attr("controlsList", "nodownload") 
                                        attr("playsinline", "true")
                                        attr("preload", "auto") // Revertido de metadata
                                        // Eliminado fragmento de tiempo para asegurar inicio en 0.0
                                        attr("src", tutorials[activeTutorialIndex].imageSrc)
                                        classes("animate-fadeIn")
                                        style {
                                            property("width", "100%")
                                            property("height", "100%")
                                            property("object-fit", "contain")
                                            property("border-radius", "1.8rem")
                                            property("background-color", "#000")
                                            property("z-index", "40")
                                            property("position", "relative")
                                            property("cursor", "pointer")
                                        }
                                    },
                                    content = {}
                                )
                            }

                            // Elementos decorativos movidos al final y con pointer-events-none absoluto
                            Div(attrs = { 
                                classes("absolute", "top-4", "left-1/2", "-translate-x-1/2", "w-3", "h-3", "bg-black", "rounded-full", "z-30", "border", "border-gray-700", "pointer-events-none") 
                            })
                            Div(attrs = { 
                                classes("absolute", "inset-0", "z-20", "pointer-events-none", "bg-gradient-to-tr", "from-white/5", "via-transparent", "to-white/10") 
                            })
                        }

                        // Controles - SIEMPRE DEBAJO DE LA IMAGEN Y CENTRADOS
                        CarouselControls(
                            activeIndex = activeTutorialIndex,
                            itemsCount = tutorials.size,
                            onPrev = { if (activeTutorialIndex > 0) activeTutorialIndex-- },
                            onNext = { if (activeTutorialIndex < tutorials.size - 1) activeTutorialIndex++ },
                            onSelect = { activeTutorialIndex = it }
                        )
                    }
                }
            }
        }

        // --- QUICK START GUIDE ---
        Section(attrs = { classes("py-32", "bg-white", "dark:bg-medical-bgDark") }) {
            Div(attrs = { classes("max-w-7xl", "mx-auto", "px-6") }) {
                Div(attrs = { classes("text-center", "mb-20") }) {
                    Span(attrs = { 
                        classes("text-medical-primary", "dark:text-medical-primaryDark", "font-bold", "text-sm", "uppercase", "tracking-widest", "mb-4", "block") 
                    }) {
                        Text("Simplicidad")
                    }
                    H2(attrs = { classes("text-3xl", "md:text-5xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-6") }) {
                        Text("Evoluciona tu consulta en 3 pasos")
                    }
                    P(attrs = { classes("text-xl", "text-gray-600", "dark:text-gray-400", "max-w-2xl", "mx-auto") }) {
                        Text("Diseñamos Dr. Recipe para que puedas empezar a digitalizar tus documentos de forma inmediata, sin complicaciones técnicas.")
                    }
                }

                Div(attrs = { classes("grid", "grid-cols-1", "md:grid-cols-3", "gap-12", "mt-16") }) {
                    QuickStartStep(
                        number = "01",
                        title = "Descarga la App",
                        description = "Encuéntranos en la Play Store como Dr. Recipe. Compatible con dispositivos Android 11 en adelante.",
                        icon = "download",
                        delay = "0s"
                    )
                    QuickStartStep(
                        number = "02",
                        title = "Regístrate y Verifica",
                        description = "Regístrate, verifícate cargando tu documentación (MSDS) y activa tu firma digital con QR seguro.",
                        icon = "how_to_reg",
                        delay = "0.2s"
                    )
                    QuickStartStep(
                        number = "03",
                        title = "Empieza a Consultar",
                        description = "Genera tu primer récipe, informe o reposo. Envía el PDF seguro a tu paciente en segundos.",
                        icon = "task_alt",
                        delay = "0.4s"
                    )
                }
            }
        }

        // --- FOOTER CTA ---
        Section(attrs = { classes("py-24", "bg-medical-primary", "dark:bg-medical-primaryDark") }) {
            Div(attrs = { classes("max-w-4xl", "mx-auto", "px-6", "text-center") }) {
                H2(attrs = { classes("text-3xl", "md:text-5xl", "font-bold", "text-white", "dark:text-medical-bgDark", "mb-8") }) {
                    Text("¿Listo para digitalizar tu consulta?")
                }
                
                Div(attrs = { 
                    classes("flex", "flex-col", "sm:flex-row", "items-center", "justify-center", "gap-6") 
                }) {
                    A(href = "https://play.google.com/store/apps/details?id=com.artisancode.drrecipe", attrs = {
                        attr("target", "_blank")
                        classes("inline-block", "transition-all", "hover:scale-105")
                    }) {
                        Img(src = "disponible_play.svg", attrs = { classes("h-20", "auto") })
                    }

                    Div(attrs = { 
                        classes("flex", "items-center", "gap-3", "text-white/90", "dark:text-medical-bgDark/90", "text-sm", "font-medium") 
                    }) {
                        Span(attrs = { classes("material-symbols-outlined") }) { Text("verified") }
                        Text("15 días de prueba gratuita")
                    }
                }
            }
        }

        // --- FOOTER ---
        SiteFooter(
            onNavigateToTerms = onNavigateToTerms,
            onNavigateToPrivacy = onNavigateToPrivacy
        )

        // --- FAB: SCROLL TO TOP ---
        if (showScrollTop) {
            Div(attrs = {
                classes(
                    "fixed", "bottom-8", "right-8", "z-40", 
                    "w-14", "h-14", "bg-medical-primary", "text-white", 
                    "rounded-2xl", "shadow-2xl", "flex", "items-center", "justify-center", 
                    "cursor-pointer", "transition-all", "hover:scale-110", "active:scale-95", 
                    "animate-bounce"
                )
                onClick { 
                    window.scrollTo(0.0, 0.0)
                }
            }) {
                Span(attrs = { classes("material-symbols-outlined", "text-3xl") }) { Text("keyboard_arrow_up") }
            }
        }
    }
}

@Composable
fun QuickStartStep(number: String, title: String, description: String, icon: String, delay: String) {
    Div(attrs = { 
        classes("flex", "flex-col", "items-center", "text-center", "p-8", "rounded-[2.5rem]", "bg-gray-50", "dark:bg-medical-surfaceDark", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark", "transition-all", "hover:shadow-xl", "hover:-translate-y-2", "reveal") 
        style { property("animation-delay", delay) }
    }) {
        Div(attrs = { classes("relative", "mb-8") }) {
            // Número decorativo
            Span(attrs = { 
                classes("absolute", "-top-2", "-right-2", "w-8", "h-8", "bg-medical-primary", "dark:bg-medical-primaryDark", "text-white", "dark:text-medical-bgDark", "rounded-full", "flex", "items-center", "justify-center", "font-bold", "text-xs", "shadow-lg") 
            }) { Text(number) }
            
            // Icono
            Div(attrs = { classes("w-20", "h-20", "bg-white", "dark:bg-medical-bgDark", "rounded-3xl", "shadow-md", "flex", "items-center", "justify-center", "text-medical-primary", "dark:text-medical-successDark", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark") }) {
                Span(attrs = { classes("material-symbols-outlined", "text-4xl") }) { Text(icon) }
            }
        }
        
        H3(attrs = { classes("text-2xl", "font-bold", "text-gray-900", "dark:text-medical-onSurfaceDark", "mb-4") }) {
            Text(title)
        }
        P(attrs = { classes("text-gray-600", "dark:text-gray-400", "leading-relaxed") }) {
            Text(description)
        }
    }
}

@Composable
fun CarouselControls(
    activeIndex: Int,
    itemsCount: Int,
    onPrev: () -> Unit,
    onNext: () -> Unit,
    onSelect: (Int) -> Unit
) {
    // Botones de Navegación
    Div(attrs = { classes("flex", "gap-4") }) {
        Button(attrs = {
            // Se usa el surfaceDark (Gris Plomo de la App) en vez de bgDark para que armonicen
            classes("w-10", "h-10", "flex", "items-center", "justify-center", "rounded-full", "bg-white", "dark:bg-medical-surfaceDark", "shadow-md", "text-medical-primary", "dark:text-medical-onSurfaceDark", "hover:scale-110", "hover:shadow-lg", "active:scale-95", "transition-all", "disabled:opacity-20", "disabled:hover:scale-100", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark")
            if (activeIndex == 0) attr("disabled", "true")
            onClick { onPrev() }
        }) {
            Span(attrs = { classes("material-symbols-outlined", "text-xl") }) { Text("arrow_back") }
        }
        Button(attrs = {
            classes("w-10", "h-10", "flex", "items-center", "justify-center", "rounded-full", "bg-white", "dark:bg-medical-surfaceDark", "shadow-md", "text-medical-primary", "dark:text-medical-onSurfaceDark", "hover:scale-110", "hover:shadow-lg", "active:scale-95", "transition-all", "disabled:opacity-20", "disabled:hover:scale-100", "border", "border-gray-100", "dark:border-medical-surfaceVariantDark")
            if (activeIndex == itemsCount - 1) attr("disabled", "true")
            onClick { onNext() }
        }) {
            Span(attrs = { classes("material-symbols-outlined", "text-xl") }) { Text("arrow_forward") }
        }
    }

    // Indicadores (Puntos)
    Div(attrs = { classes("flex", "gap-2") }) {
        repeat(itemsCount) { index ->
            Div(attrs = {
                classes("h-2", "rounded-full", "transition-all", "duration-300", "cursor-pointer")
                // Truco Tailwind para evitar depender de ThemeState: Usar una clase base y otra en dark:
                // Pero como es un background color que definías inline, lo pasamos a clases Tailwind:
                if (activeIndex == index) {
                    classes("w-6", "bg-medical-tertiary", "dark:bg-medical-successDark")
                } else {
                    classes("w-2", "bg-gray-300", "dark:bg-medical-surfaceVariantDark")
                }
                onClick { onSelect(index) }
            })
        }
    }
}

data class FeatureData(val title: String, val description: String, val imageSrc: String)
